import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import Slider from 'react-slick';
import { useWindowSize } from '@magento/peregrine/lib/hooks/useWindowSize';
import LinkButton from '@magento/venia-ui/lib/components/LinkButton';
import { useProductImageCarousel } from '@magento/peregrine/lib/talons/ProductImageCarousel/useProductImageCarousel';
import defaultClasses from './productSlider.module.css';
import ZoomSliderModal from './zoomSliderModal';
import { InfoIcon, PrevArrow, NextArrow } from './Icons';
import { Image, ActiveInfoIcon, VideoIcon, ChevronLeftIcon, ChevronRightIcon } from '../../../../components';
import ThreeSixtyModal from './threeSixtyModal';
import { act } from 'react-test-renderer';
import configure from '../../../../assets/configureIcon.svg';
import globeIcon from '../../../../assets/360.svg';
var IMAGE_WIDTH = 300;

var extractSortNumber = function extractSortNumber(url, defaultSmartString, defaultSmartStringSelected) {
  var regex = /\/([^\/_]*_(\d+))/;
  var result = url.match(regex);

  if (result) {
    if (parseInt(result[2]) < 49) {
      return 200 + parseInt(result[2]);
    } else if (parseInt(result[2]) < 59) {
      return parseInt(result[2]);
    } else {
      return 100 + parseInt(result[2]);
    }
  } else {
    // console.log("No number preceded by underscore was found in the URL after the last '/'");
    return 1000;
  }
};

var extractDefaultSmartString = function extractDefaultSmartString(images) {
  var regex = /\/([^\/_]*)_1[\._]/;

  for (var i = 0; i < images.length; i++) {
    var result = images[i].url.match(regex);

    if (result) {
      return result[1];
    }
  }

  return false;
};

var ProductSlider = function ProductSlider(_ref) {
  var images = _ref.images,
      threeSixtyLink = _ref.threeSixtyLink,
      scaledImages = _ref.scaledImages,
      toggleDrawer = _ref.toggleDrawer,
      videoGallery = _ref.videoGallery,
      productName = _ref.productName,
      _ref$defaultSmartStri = _ref.defaultSmartStringSelected,
      defaultSmartStringSelected = _ref$defaultSmartStri === void 0 ? true : _ref$defaultSmartStri,
      configuredSmartString = _ref.configuredSmartString,
      optionSelections = _ref.optionSelections,
      setOptionSelections = _ref.setOptionSelections,
      options = _ref.options,
      isMobile = _ref.isMobile,
      setProductConfiguration = _ref.setProductConfiguration;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      activeSlider = _useState2[0],
      setActiveSlider = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      zoomedImage = _useState4[0],
      setZoomedImage = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isOpenThreeSixty = _useState6[0],
      setIsOpenThreeSixty = _useState6[1];

  var _useState7 = useState(null),
      _useState8 = _slicedToArray(_useState7, 2),
      nav1 = _useState8[0],
      setNav1 = _useState8[1];

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      nav2 = _useState10[0],
      setNav2 = _useState10[1];

  var _useState11 = useState(null),
      _useState12 = _slicedToArray(_useState11, 2),
      numberOfSlidesToShow = _useState12[0],
      setNumberOfSlidesToShow = _useState12[1];

  var _useState13 = useState(null),
      _useState14 = _slicedToArray(_useState13, 2),
      numberOfSlidesToShowSmallDevice = _useState14[0],
      setNumberOfSlidesToShowSmallDevice = _useState14[1];

  var _useState15 = useState(null),
      _useState16 = _slicedToArray(_useState15, 2),
      smallSlideImgActive = _useState16[0],
      setSmallSlideImgActive = _useState16[1];

  var _useState17 = useState(null),
      _useState18 = _slicedToArray(_useState17, 2),
      configuredAsShownImage = _useState18[0],
      setConfiguredAsShownImage = _useState18[1];

  var shownInfoPopup = useMemo(function () {
    var _images$activeSlider, _images$activeSlider$;

    if (!((_images$activeSlider = images[activeSlider]) !== null && _images$activeSlider !== void 0 && (_images$activeSlider$ = _images$activeSlider.url) !== null && _images$activeSlider$ !== void 0 && _images$activeSlider$.includes(configuredSmartString))) return React.createElement("div", {
      className: defaultClasses.infoPopup
    }, React.createElement("p", {
      className: "text-center"
    }, "Image shown does not correspond to selected options."));
    return React.createElement(React.Fragment, null);
  }, [images, activeSlider, configuredSmartString]);

  var _useWindowSize = useWindowSize(),
      innerWidth = _useWindowSize.innerWidth;

  var mainSlider = useRef();
  var smallSlider = useRef();
  useEffect(function () {
    if (zoomedImage !== null) document.body.style.overflow = 'hidden';else document.body.style.removeProperty('overflow');
  }, [zoomedImage]);
  var defaultSmartString = extractDefaultSmartString(images);
  images.sort(function (x, y) {
    var xSortNum = extractSortNumber(x.url, defaultSmartString, defaultSmartStringSelected);
    var ySortNum = extractSortNumber(y.url, defaultSmartString, defaultSmartStringSelected);
    return xSortNum < ySortNum ? -1 : 1;
  });

  var _useProductImageCarou = useProductImageCarousel({
    images: images,
    imageWidth: IMAGE_WIDTH
  }),
      sortedImages = _useProductImageCarou.sortedImages;

  useEffect(function () {
    var findIndex = sortedImages === null || sortedImages === void 0 ? void 0 : sortedImages.findIndex(function (el) {
      var _el$url;

      return el === null || el === void 0 ? void 0 : (_el$url = el.url) === null || _el$url === void 0 ? void 0 : _el$url.includes(configuredSmartString);
    });
    if (findIndex !== -1) nav1 === null || nav1 === void 0 ? void 0 : nav1.slickGoTo(findIndex);else if (!(smallSlideImgActive !== null && smallSlideImgActive !== void 0 && smallSlideImgActive.includes(configuredSmartString))) nav1 === null || nav1 === void 0 ? void 0 : nav1.slickGoTo(0);
  }, [configuredSmartString, smallSlideImgActive, configuredAsShownImage, sortedImages]);
  var customSortOptions = useMemo(function () {
    var _optionsCopy;

    if (!(options !== null && options !== void 0 && options.length)) return [];

    var optionsCopy = _toConsumableArray(options);

    var findByCode = function findByCode(code) {
      return optionsCopy.find(function (opt) {
        return (opt === null || opt === void 0 ? void 0 : opt.attribute_code) === code;
      });
    };

    var finish = findByCode('hf_finish');
    var accentFinish = findByCode('hf_accent_finish');
    if (!accentFinish) return optionsCopy;
    optionsCopy = (_optionsCopy = optionsCopy) === null || _optionsCopy === void 0 ? void 0 : _optionsCopy.filter(function (opt) {
      return (opt === null || opt === void 0 ? void 0 : opt.attribute_code) !== 'hf_finish' && (opt === null || opt === void 0 ? void 0 : opt.attribute_code) !== 'hf_accent_finish';
    });
    if (finish) optionsCopy.push(finish);
    if (accentFinish) optionsCopy.push(accentFinish);
    return optionsCopy;
  }, [options]);
  var CustomPrevArrow = useCallback(function (_ref2) {
    var className = _ref2.className,
        style = _ref2.style,
        onClick = _ref2.onClick;
    return React.createElement(PrevArrow, {
      className: className,
      style: style,
      onClick: onClick
    });
  }, []);
  var CustomNextArrow = useCallback(function (_ref3) {
    var className = _ref3.className,
        style = _ref3.style,
        onClick = _ref3.onClick;
    return React.createElement(NextArrow, {
      className: className,
      style: style,
      onClick: onClick
    });
  }, []);
  var handleThreeSixtyModal = useCallback(function () {
    return setIsOpenThreeSixty(function (prevState) {
      return !prevState;
    });
  }, []);
  var CustomSmallSliderPrevArrow = useCallback(function (props) {
    var className = props.className,
        style = props.style,
        onClick = props.onClick;
    return React.createElement("div", {
      className: className,
      style: style,
      onClick: onClick
    }, React.createElement(ChevronLeftIcon, null));
  }, []);
  var CustomSmallSliderNextArrow = useCallback(function (props) {
    var className = props.className,
        style = props.style,
        onClick = props.onClick;
    return React.createElement("div", {
      className: className,
      style: style,
      onClick: onClick
    }, React.createElement(ChevronRightIcon, null));
  }, []);
  var mainSliderSettings = {
    speed: 500,
    infinite: true,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: function beforeChange(curr, next) {
      return setActiveSlider(next);
    },
    nextArrow: React.createElement(CustomNextArrow, null),
    prevArrow: React.createElement(CustomPrevArrow, null)
  };
  var smallSliderSettings = {
    slidesToShow: numberOfSlidesToShow,
    focusOnSelect: true,
    arrows: true,
    nextArrow: React.createElement(CustomSmallSliderNextArrow, null),
    prevArrow: React.createElement(CustomSmallSliderPrevArrow, null),
    infinite: true,
    responsive: [{
      breakpoint: 1566,
      settings: {
        slidesToShow: numberOfSlidesToShowSmallDevice
      }
    }, {
      breakpoint: 767,
      settings: {
        slidesToShow: 0,
        slidesToScroll: 1,
        arrows: false,
        dots: true
      }
    }]
  };

  var handleCloseSliderModal = function handleCloseSliderModal() {
    return setZoomedImage(null);
  };

  var _useState19 = useState(defaultClasses.halfOpacity),
      _useState20 = _slicedToArray(_useState19, 2),
      wrapperAdditional = _useState20[0],
      setWrapperAdditional = _useState20[1];

  var _useState21 = useState(''),
      _useState22 = _slicedToArray(_useState21, 2),
      smallSliderAdditional = _useState22[0],
      setSmallSliderAdditional = _useState22[1];

  useEffect(function () {
    return setTimeout(function () {
      return setWrapperAdditional(null);
    }, 500);
  }, [setWrapperAdditional]);
  useEffect(function () {
    setWrapperAdditional(defaultClasses.halfOpacity);
    setTimeout(function () {
      return setWrapperAdditional(null);
    }, 500);
  }, [images]);
  useEffect(function () {
    if (images.length > 3 && (innerWidth < 1560 || innerWidth > 767)) {
      setSmallSliderAdditional(defaultClasses.smallSliderWidth);
    } else if (images.length <= 5 && innerWidth < 768) {
      setSmallSliderAdditional(defaultClasses.smallSliderVariableWidth);
    } else {
      if (smallSliderAdditional !== '') setSmallSliderAdditional('');
    }

    setNumberOfSlidesToShow(sortedImages.length < 5 ? sortedImages.length : 5);
    setNumberOfSlidesToShowSmallDevice(sortedImages.length < 3 ? sortedImages.length : 3);
  }, [images, innerWidth]);
  useEffect(function () {
    setNav1(mainSlider);
    setNav2(smallSlider);
    setActiveSlider(0);
  }, []);

  var findValueByDiff = function findValueByDiff(item, values, diff) {
    return values === null || values === void 0 ? void 0 : values.find(function (val) {
      return diff.some(function (d) {
        var _val$label, _val$label2;

        return (item === null || item === void 0 ? void 0 : item.attribute_code) === 'hf_smart_string_synonym' ? val === null || val === void 0 ? void 0 : (_val$label = val.label) === null || _val$label === void 0 ? void 0 : _val$label.includes(d === null || d === void 0 ? void 0 : d.slice(0, 2)) : val === null || val === void 0 ? void 0 : (_val$label2 = val.label) === null || _val$label2 === void 0 ? void 0 : _val$label2.includes(d);
      });
    });
  };

  var configureAsShown = useCallback(function () {
    if (customSortOptions !== null && customSortOptions !== void 0 && customSortOptions.length) {
      var _images$activeSlider2;

      var activeImage = (_images$activeSlider2 = images[activeSlider]) === null || _images$activeSlider2 === void 0 ? void 0 : _images$activeSlider2.url;

      if (activeImage) {
        var _activeImage$split, _activeImage$split$at, _activeImage$split$at2;

        var smartString = activeImage === null || activeImage === void 0 ? void 0 : (_activeImage$split = activeImage.split('/')) === null || _activeImage$split === void 0 ? void 0 : (_activeImage$split$at = _activeImage$split.at(-1)) === null || _activeImage$split$at === void 0 ? void 0 : (_activeImage$split$at2 = _activeImage$split$at.split('_')) === null || _activeImage$split$at2 === void 0 ? void 0 : _activeImage$split$at2[0];

        if (smartString) {
          setProductConfiguration(smartString);
          setConfiguredAsShownImage(activeImage);
        }
      }
    }
  }, [activeSlider, images, customSortOptions, configuredSmartString, optionSelections]);
  var smallSliderContent = React.createElement("div", {
    className: defaultClasses.smallSliderWrapper
  }, React.createElement(Slider, _extends({
    asNavFor: nav1,
    ref: function ref(slider) {
      return smallSlider = slider;
    },
    className: "".concat(defaultClasses.smallSlider, " ").concat(smallSliderAdditional)
  }, smallSliderSettings), sortedImages === null || sortedImages === void 0 ? void 0 : sortedImages.map(function (item, idx) {
    return React.createElement(Image, {
      key: "small-".concat(idx),
      alt: productName,
      resource: item === null || item === void 0 ? void 0 : item.url,
      classes: {
        image: defaultClasses.image
      },
      onClick: function onClick() {
        return setSmallSlideImgActive(item === null || item === void 0 ? void 0 : item.url);
      }
    });
  })));
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: defaultClasses.productSliderWrapper
  }, React.createElement(Slider, _extends({
    asNavFor: nav2,
    className: defaultClasses.sliderWrapper + ' ' + wrapperAdditional,
    ref: function ref(slider) {
      return mainSlider = slider;
    }
  }, mainSliderSettings), sortedImages === null || sortedImages === void 0 ? void 0 : sortedImages.map(function (item, idx) {
    return React.createElement(Image, {
      key: idx,
      alt: productName,
      resource: item === null || item === void 0 ? void 0 : item.url,
      classes: {
        image: defaultClasses.image
      },
      onClick: function onClick() {
        return setZoomedImage(activeSlider);
      }
    });
  })), React.createElement("div", {
    className: "flex items-center relative justify-between marker_md_mb-0 md_mb-20 mb-2 gap-3"
  }, !isMobile && shownInfoPopup, React.createElement("div", {
    className: "flex items-center gap-2 cursor-pointer md_mt-[20px]",
    role: "button",
    onClick: configureAsShown
  }, React.createElement("img", {
    style: {
      height: '32px'
    },
    className: "w-fit cursor-pointer",
    src: configure,
    alt: "configure-image"
  }), React.createElement("div", {
    className: "text-xs w-[60px] h-auto"
  }, "Configure as Shown")), !isMobile && smallSliderContent, React.createElement("div", {
    className: defaultClasses.zoomWrapper
  }, videoGallery !== null && videoGallery !== void 0 && videoGallery.length ? React.createElement("div", {
    className: "mr-5 cursor-pointer",
    onClick: function onClick() {
      return toggleDrawer('videoModal');
    },
    role: "button"
  }, React.createElement(VideoIcon, null)) : null, threeSixtyLink && threeSixtyLink !== '360' ? React.createElement(LinkButton, {
    onClick: handleThreeSixtyModal,
    className: "md_-mb-2.5 flex flex-col gap-2",
    "aria-label": "Open three sixty modal"
  }, React.createElement("img", {
    style: {
      height: '45px',
      width: 'auto'
    },
    src: globeIcon,
    alt: "360-icon"
  })) : null)), isMobile && React.createElement(React.Fragment, null, smallSliderContent, shownInfoPopup)), zoomedImage !== null && React.createElement(ZoomSliderModal, {
    images: images,
    closeModal: handleCloseSliderModal,
    activeSlider: activeSlider,
    setActiveSlider: setActiveSlider,
    productName: productName
  }), isOpenThreeSixty && React.createElement(ThreeSixtyModal, {
    isOpen: isOpenThreeSixty,
    productName: productName,
    link: threeSixtyLink,
    closeModal: handleThreeSixtyModal
  }));
};

export default ProductSlider;